/**
 * Thin fetch wrapper for orion-fe.
 *
 * Why not use `fetch` directly?
 * - **Deduplication of in-flight GETs**: React 18 strict-mode and
 *   naive effect patterns happily fire the same GET twice. We dedupe
 *   by `${dispatcher} ${method} ${url}` while the request is in flight,
 *   then clear the cache once it settles. Applies to BOTH the
 *   mock-aware `request()` path and the real-only `realFetch()` path
 *   so v2 submissions, brokerages, and any other real endpoint share
 *   the same protection. Only GETs are deduped; mutating verbs
 *   (POST/PUT/PATCH/DELETE) always go through.
 * - **Mock switch**: `VITE_USE_MOCKS=true` (default during dev) makes
 *   the wrapper resolve against in-memory handlers in
 *   `mock-handlers.ts` without ever touching the network. Flip the
 *   env var to `false` to hit `VITE_ORION_API` for real (same name
 *   orion-frontend used; pipelines + Amplify already inject it).
 * - **Simulated latency**: `withDelay(ms, value)` lets mock handlers
 *   feel like real I/O — wires, loaders, and save spinners all show
 *   meaningful UX.
 *
 * Public surface mirrors `fetch`:
 *   request(input, init?) => Promise<Response>
 *
 * Endpoint modules parse the response and return typed data; the raw
 * `request()` helper is exported for advanced callers.
 */
import { resolveMockRequest } from "./mock-handlers";

const USE_MOCKS = (import.meta.env.VITE_USE_MOCKS ?? "true").toString().toLowerCase() !== "false";
// Strip trailing slash so callers can pass paths starting with "/" without
// producing "//v2/...". Matches what `src/auth/api.ts` does for the auth
// endpoints that bypass this client.
const API_BASE_URL = (import.meta.env.VITE_ORION_API ?? import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

// ──────────────────────────── Auth glue ───────────────────────
// The auth module owns the token; we just attach it to outbound
// requests and emit an event when the server says the token is no
// longer valid. Avoiding a hard import keeps this file unaware of
// React and prevents a circular dep with the auth context.

let currentToken: string | null = null;

/** Called by AuthProvider whenever the token changes (login, logout,
 *  bootstrap, 401 cleanup). Pass `null` to clear. */
export function setRequestToken(token: string | null): void {
    currentToken = token;
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Subscribe to "the server returned 401". Returns an unsubscribe fn.
 *  AuthProvider uses this to flip back to unauthenticated state. */
export function subscribeToUnauthorized(listener: UnauthorizedListener): () => void {
    unauthorizedListeners.add(listener);
    return () => unauthorizedListeners.delete(listener);
}

function notifyUnauthorized(): void {
    for (const fn of unauthorizedListeners) {
        try {
            fn();
        } catch {
            // listeners must not break the request pipeline
        }
    }
}

// ──────────────────────────── Dedupe ──────────────────────────

const inFlight = new Map<string, Promise<Response>>();

/** Build a dedupe key, or return null if the request shouldn't be
 *  deduped (mutating verbs always go through). The `dispatcher` prefix
 *  keeps the mock-aware path and the real-only path in separate
 *  buckets so a `request("/x")` and a `realFetch("/x")` racing at the
 *  same time don't accidentally share a Response. */
function dedupeKey(method: string, url: string, dispatcher: "M" | "R"): string | null {
    if (method.toUpperCase() !== "GET") return null;
    return `${dispatcher} ${method} ${url}`;
}

/** Run `start` once per in-flight key. Subsequent callers get a clone
 *  of the same Response (a Response body is a single-use stream — see
 *  the long comment below). The cache entry is dropped once the
 *  promise settles. */
function dedupedFetch(key: string | null, start: () => Promise<Response>): Promise<Response> {
    if (!key) return start();
    const existing = inFlight.get(key);
    if (existing) return existing.then((r) => r.clone());
    const promise = start();
    inFlight.set(key, promise);
    promise.finally(() => inFlight.delete(key));
    // 401 → notify the auth module so it can flush state and let the
    // ProtectedRoute redirect. Done here so both dispatchers benefit.
    promise.then(
        (r) => {
            if (r.status === 401 && currentToken) notifyUnauthorized();
        },
        () => undefined,
    );
    // Hand each caller (including the first) a private clone so the
    // body stream isn't shared across consumers.
    return promise.then((r) => r.clone());
}

// ──────────────────────────── Latency ─────────────────────────

export function withDelay<T>(ms: number, value: T): Promise<T> {
    return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// ──────────────────────────── Core ────────────────────────────

export function request(input: RequestInfo, init?: RequestInit): Promise<Response> {
    const method = (init?.method ?? "GET").toUpperCase();
    const url = typeof input === "string" ? input : input.url;
    const key = dedupeKey(method, url, "M");

    return dedupedFetch(key, () => {
        // Inject the Authorization header on real requests (mocks ignore
        // headers anyway). We merge with caller-provided headers so a route
        // that needs a different auth scheme can still override.
        const realInit = currentToken
            ? {
                  ...init,
                  headers: { ...(init?.headers ?? {}), Authorization: `Bearer ${currentToken}` },
              }
            : init;
        return USE_MOCKS
            ? Promise.resolve(resolveMockRequest(input, init))
            : fetch(typeof input === "string" ? `${API_BASE_URL}${input}` : input, realInit);
    });
    // We don't try to silently refresh the token on 401 because the
    // backend doesn't expose a refresh grant; the user has to sign in
    // again. The 401 listener fan-out happens inside `dedupedFetch`.
}

// ────────────────────────── JSON helpers ──────────────────────

export async function getJson<T>(path: string): Promise<T> {
    const response = await request(path);
    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, await errorDetail(response));
    }
    return (await response.json()) as T;
}

export async function sendJson<T>(path: string, method: "POST" | "PUT" | "PATCH" | "DELETE", body?: unknown): Promise<T> {
    const response = await request(path, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, await errorDetail(response));
    }
    // 204 No Content — common for DELETE handlers that have nothing to
    // return. `response.json()` on an empty body throws "Unexpected
    // end of input"; guard for it explicitly.
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
}

// ──────────────────────── Real-API helpers ────────────────────
//
// Some endpoints (the v2 submissions API) must always hit the real
// backend even when `VITE_USE_MOCKS=true` — the broker autocomplete
// still relies on the mock layer because the backend hasn't shipped
// those endpoints yet. These helpers bypass the mock dispatch but keep
// the auth-header injection and 401 broadcast logic in sync.

function realFetch(path: string, init?: RequestInit): Promise<Response> {
    const method = (init?.method ?? "GET").toUpperCase();
    const url = `${API_BASE_URL}${path}`;
    const key = dedupeKey(method, url, "R");

    return dedupedFetch(key, () => {
        const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) };
        if (currentToken) headers.Authorization = `Bearer ${currentToken}`;
        return fetch(url, { ...init, headers });
    });
}

export async function getJsonReal<T>(path: string): Promise<T> {
    const response = await realFetch(path);
    if (!response.ok) throw new ApiError(response.status, response.statusText, await errorDetail(response));
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
}

export async function sendJsonReal<T>(path: string, method: "POST" | "PUT" | "PATCH" | "DELETE", body?: unknown): Promise<T> {
    const response = await realFetch(path, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) throw new ApiError(response.status, response.statusText, await errorDetail(response));
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
}

/**
 * Fetch a binary document (PDF) from the real backend — used by the
 * document-generation endpoints (Quote / Binder). Returns the response
 * Blob; throws `ApiError` on a non-2xx so callers can surface the
 * backend's message (e.g. the final_premium gate on the Quote).
 */
export async function fetchDocumentReal(
    path: string,
    method: "GET" | "POST" = "POST",
    body?: unknown,
): Promise<Blob> {
    const init: RequestInit = { method };
    if (body !== undefined) {
        init.headers = { "Content-Type": "application/json" };
        init.body = JSON.stringify(body);
    }
    const response = await realFetch(path, init);
    if (!response.ok) throw new ApiError(response.status, response.statusText, await errorDetail(response));
    return response.blob();
}

/** The filename carried in a `Content-Disposition` header, or null.
 *
 * Handles both the plain form (`attachment; filename="a b.xlsx"`, quoted
 * or bare) and the RFC 5987 form (`filename*=UTF-8''a%20b.xlsx`), which
 * wins when both are present since it's the one that survives non-ASCII.
 */
export function filenameFromContentDisposition(header: string | null): string | null {
    if (!header) return null;
    const extended = /filename\*\s*=\s*(?:UTF-8''|utf-8'')?([^;]+)/.exec(header);
    if (extended) {
        try {
            return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ""));
        } catch {
            /* fall through to the plain form */
        }
    }
    const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header);
    return plain ? plain[1].trim() : null;
}

/**
 * Like `fetchDocumentReal`, but also surfaces the download filename the
 * backend chose (`Content-Disposition`). Used where the server names the
 * file meaningfully — e.g. the pricing-model export's
 * `Pricing Model - {FNI} - {date}.xlsx`. `filename` is null when the
 * header is absent or not exposed to the browser (CORS), so callers keep
 * a local fallback.
 */
export async function fetchNamedDocumentReal(
    path: string,
    method: "GET" | "POST" = "GET",
): Promise<{ blob: Blob; filename: string | null }> {
    const response = await realFetch(path, { method });
    if (!response.ok) throw new ApiError(response.status, response.statusText, await errorDetail(response));
    return {
        blob: await response.blob(),
        filename: filenameFromContentDisposition(response.headers.get("Content-Disposition")),
    };
}

/**
 * Multipart POST against the real backend — used by file-upload
 * endpoints (e.g. `POST /v2/submissions/from-upload`).
 *
 * We deliberately DON'T set `Content-Type` on the request: when the
 * body is a `FormData` instance the browser appends
 * `multipart/form-data` with the correct boundary parameter on its
 * own. Setting it manually would prepend a header missing the
 * boundary and the backend would reject the request.
 */
export async function uploadMultipartReal<T>(path: string, formData: FormData): Promise<T> {
    const response = await realFetch(path, {
        method: "POST",
        body: formData,
    });
    if (!response.ok) throw new ApiError(response.status, response.statusText, await errorDetail(response));
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
}

export class ApiError extends Error {
    /** Backend-provided message (FastAPI `detail`), when present. */
    detail?: string;
    constructor(
        public status: number,
        public statusText: string,
        detail?: string,
    ) {
        // Prefer the backend's human message so callers can surface it
        // directly (e.g. "A policy already exists for this submission.").
        super(detail ?? `Request failed: ${status} ${statusText}`);
        this.name = "ApiError";
        this.detail = detail;
    }
}

/** Best-effort read of FastAPI's `{ "detail": "..." }` error body. Returns
 *  undefined for non-JSON / array-detail (422 validation) payloads, so the
 *  caller falls back to the generic status message. */
async function errorDetail(response: Response): Promise<string | undefined> {
    try {
        const data = (await response.clone().json()) as unknown;
        if (data && typeof data === "object" && "detail" in data) {
            const d = (data as { detail: unknown }).detail;
            if (typeof d === "string") return d;
        }
    } catch {
        /* non-JSON body */
    }
    return undefined;
}

// ───────────────────────── Testing helpers ────────────────────

/** Clear the in-flight dedupe cache. Intended for tests. */
export function __clearInFlight(): void {
    inFlight.clear();
}
